import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { AlertCircle, CheckCircle2, Loader2, Headphones, ArrowLeft } from 'lucide-react';

const API_URL = typeof window !== 'undefined' ? window.location.origin : '';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSuccess(true);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        'Errore durante l\'invio dell\'email. Riprova.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F4F5] flex items-center justify-center p-4">
      <div className="w-full max-w-md animate-fade-up">
        {/* Logo Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#002FA7] text-white mb-4">
            <Headphones className="w-8 h-8" />
          </div>
          <h1 className="text-3xl font-medium tracking-tight text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            IT Helpdesk
          </h1>
          <p className="text-[#71717A] mt-2">Piattaforma di Assistenza Intelligente</p>
        </div>

        <Card className="border border-[#E4E4E7] shadow-none">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-2xl font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Password dimenticata
            </CardTitle>
            <CardDescription>
              Inserisci la tua email e ti invieremo un link per reimpostare la password
            </CardDescription>
          </CardHeader>
          <CardContent>
            {success ? (
              <div className="text-center space-y-4">
                <div className="flex justify-center">
                  <CheckCircle2 className="w-12 h-12 text-green-500" />
                </div>
                <p className="text-[#0F172A] font-medium">Email inviata!</p>
                <p className="text-sm text-[#71717A]">
                  Se l'indirizzo <strong>{email}</strong> è registrato nel sistema, riceverai a breve un'email con il link per reimpostare la password.
                </p>
                <p className="text-xs text-[#71717A]">
                  Controlla anche la cartella spam. Il link scade dopo 1 ora.
                </p>
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 text-sm text-[#002FA7] hover:underline font-medium mt-4"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Torna al login
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="flex items-center gap-2 p-3 text-sm text-[#DC2626] bg-red-50 border border-red-200 rounded-md">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {error}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="nome@esempio.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                    className="border-[#E4E4E7] focus:border-[#002FA7] focus:ring-[#002FA7]"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#002FA7] hover:bg-[#002FA7]/90 text-white font-medium"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Invio in corso...
                    </>
                  ) : (
                    'Invia link di reset'
                  )}
                </Button>

                <div className="text-center">
                  <Link
                    to="/login"
                    className="inline-flex items-center gap-2 text-sm text-[#71717A] hover:text-[#002FA7]"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Torna al login
                  </Link>
                </div>
              </form>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-xs text-[#71717A] mt-6">
          Powered by IKONET Solutions
        </p>
      </div>
    </div>
  );
}
